import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Transition } from "@headlessui/react";
import { CheckCircle2, PlayCircle, Sparkles } from "lucide-react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";
import { toast } from "react-toastify";
import swal from "sweetalert";

// ── Property Helpers ──
const getProductKey = (item) => item?.qbId || item?.ListID || item?.raw?.ListID || item?._id;

const getProductName = (item) =>
  item?.name ||
  item?.Name ||
  item?.fullyQualifiedName ||
  item?.FullName ||
  item?.raw?.Name ||
  item?.raw?.FullName ||
  "Item";

const getProductDesc = (item) =>
  item?.description ??
  item?.Description ??
  item?.raw?.SalesOrPurchase?.Desc ??
  item?.raw?.ItemDesc ??
  "";

const getProductRate = (item) => {
  if (item?.unitPrice != null) return Number(item.unitPrice);
  if (item?.SalesPrice != null) return Number(item.SalesPrice);
  if (item?.raw?.SalesOrPurchase?.Price != null) return Number(item.raw.SalesOrPurchase.Price);
  return 0;
};

const getTaxRate = (item) => {
  if (item?.taxRate != null) return Number(item.taxRate);
  if (item?.TaxRate != null) return Number(item.TaxRate);
  if (item?.raw?.TaxRate != null) return Number(item.raw.TaxRate);
  if (item?.rate != null) return Number(item.rate);
  if (item?.Rate != null) return Number(item.Rate);
  return getProductRate(item);
};

const getCustomerKey = (customer) =>
  customer?.qbId || customer?.ListID || customer?.raw?.ListID || customer?._id;

const getCustomerName = (customer) => {
  if (!customer) return "—";
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return (
    customer.fullyQualifiedName ||
    customer.FullName ||
    customer.raw?.FullName ||
    customer.name ||
    customer.Name ||
    customer.raw?.Name ||
    fullName ||
    customer.company ||
    customer.raw?.CompanyName ||
    "Unnamed Customer"
  );
};

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val ?? 0);

export default function QBDEstimateAdd() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const companyId = user?.id || user?._id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [formData, setFormData] = useState({
    customerListId: "",
    txnDate: new Date().toISOString().split("T")[0],
    refNumber: "",
    lines: [
      {
        itemListId: "",
        description: "",
        quantity: 1,
        rate: 0,
      },
    ],
    discount_value: 0,
    discount_type: "fixed",
    taxItemListId: "",
    taxName: "",
    taxPercent: 0,
  });

  const [errors, setErrors] = useState({});

  const fetchInitialData = async () => {
    loader(true);
    try {
      const [custRes, prodRes, taxRes] = await Promise.all([
        ApiClient.get(`qb-desktop/list/customers?companyId=${companyId}`),
        ApiClient.get(`qb-desktop/list/items?companyId=${companyId}`),
        ApiClient.get(`items?companyId=${companyId}&usage=tax`),
      ]);
      if (custRes?.success) setCustomers(custRes.data || []);
      if (prodRes?.success) setProducts(prodRes.data || []);
      if (taxRes?.success) setTaxRates(taxRes.data || []);
    } catch (err) {
      console.error(err);
    }
    loader(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Computed subtotal
  const subtotal = formData.lines.reduce((acc, l) => acc + Number(l.quantity || 0) * Number(l.rate || 0), 0);

  const discountAmt =
    formData.discount_type === "percent"
      ? (subtotal * Number(formData.discount_value || 0)) / 100
      : Number(formData.discount_value || 0);

  const taxPercent = Number(formData.taxPercent || 0);
  const taxAmt = ((subtotal - discountAmt) * taxPercent) / 100;

  const total = subtotal - discountAmt + taxAmt;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    if (field === "itemListId") {
      const product = products.find((p) => getProductKey(p) === value);
      newLines[index].itemListId = value;
      if (product) {
        newLines[index].description = getProductDesc(product);
        newLines[index].rate = getProductRate(product);
      }
    } else {
      newLines[index][field] = value;
    }
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setFormData((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        { itemListId: "", description: "", quantity: 1, rate: 0 },
      ],
    }));
  };

  const removeLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, lines: newLines }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.customerListId) errs.customerListId = "Please select a customer";
    if (!formData.txnDate) errs.txnDate = "Estimate date is required";
    if (formData.lines.length === 0) errs.lines = "At least one line item is required";
    formData.lines.forEach((l, i) => {
      if (!l.itemListId) errs[`line_${i}_item`] = `Select a product for line ${i + 1}`;
      if (!l.quantity || Number(l.quantity) <= 0) errs[`line_${i}_qty`] = `Qty must be > 0 for line ${i + 1}`;
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      swal({ icon: "error", title: "Validation Error", text: "Please fix the errors before submitting." });
      return;
    }

    setSubmitting(true);
    loader(true);
    try {
      const payload = {
        companyId: companyId,
        customerListId: formData.customerListId,
        txnDate: formData.txnDate,
        refNumber: formData.refNumber,
        lines: formData.lines.map((l) => ({
          itemListId: l.itemListId,
          description: l.description,
          quantity: Number(l.quantity),
          rate: Number(l.rate),
        })),
        // Discount fields
        discount_value: Number(formData.discount_value || 0),
        discount_type: formData.discount_type,
        discountAmount: discountAmt,
        discount_amount: discountAmt,
        // Tax fields
        taxItemListId: formData.taxItemListId || null,
        tax_percent: taxPercent,
        total_tax: taxAmt,
        totalTax: taxAmt,
        taxDetails: {
          tax_percent: taxPercent,
          tax_name: formData.taxName,
          itemListId: formData.taxItemListId,
          totalTax: taxAmt,
        },
      };

      const res = await ApiClient.post("qb-desktop/create/estimate", payload);
      if (res?.success) {
        setShowSuccessModal(true);
      } else {
        toast.error(res?.message || "Failed to create estimate");
      }
    } catch (err) {
      toast.error("An error occurred while creating the estimate");
    }
    loader(false);
    setSubmitting(false);
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigate("/qb-desktop/estimates");
  };

  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px] mx-auto py-5">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-100 px-5 sm:px-8 py-6">
          {/* Header */}
          <div className="flex gap-y-2 gap-x-3 flex-wrap justify-between items-center mb-8 border-b pb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#115B9D]">Create Estimate</h1>
              <p className="text-sm text-gray-500 mt-0.5">QuickBooks Desktop</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-medium">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(total)}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Customer & Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Customer */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Customer <span className="text-red-500">*</span>
                </label>
                <select
                  name="customerListId"
                  value={formData.customerListId}
                  onChange={handleInputChange}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white ${errors.customerListId ? "border-red-400" : "border-gray-300"}`}
                >
                  <option value="">Select Customer</option>
                  {customers.map((c, cIdx) => {
                    const cKey = getCustomerKey(c) || cIdx;
                    const cVal = getCustomerKey(c);
                    const cName = getCustomerName(c);
                    return (
                      <option key={cKey} value={cVal}>
                        {cName}
                      </option>
                    );
                  })}
                </select>
                {errors.customerListId && <p className="text-xs text-red-500">{errors.customerListId}</p>}
              </div>

              {/* Estimate Date */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Estimate Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="txnDate"
                  value={formData.txnDate}
                  onChange={handleInputChange}
                  className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all ${errors.txnDate ? "border-red-400" : "border-gray-300"}`}
                />
                {errors.txnDate && <p className="text-xs text-red-500">{errors.txnDate}</p>}
              </div>

              {/* Ref Number */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 uppercase">Ref Number</label>
                <input
                  type="text"
                  name="refNumber"
                  value={formData.refNumber}
                  onChange={handleInputChange}
                  placeholder="e.g. TEST-001"
                  className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">Line Items</h2>
                <button
                  type="button"
                  onClick={addLine}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#115B9D]/10 text-[#115B9D] text-sm font-medium hover:bg-[#115B9D]/20 transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14" /><path d="M12 5v14" />
                  </svg>
                  Add Line
                </button>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-2xl">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 w-8">#</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Product/Service</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Description</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-24">Qty</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-28">Rate</th>
                      <th className="px-4 py-3 text-right font-semibold text-gray-600 w-28">Amount</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.lines.map((line, index) => {
                      const amount = Number(line.quantity || 0) * Number(line.rate || 0);
                      return (
                        <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3 text-gray-500 text-xs">{index + 1}</td>
                          <td className="px-4 py-3">
                            <select
                              value={line.itemListId}
                              onChange={(e) => handleLineChange(index, "itemListId", e.target.value)}
                              className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white min-w-[150px] ${errors[`line_${index}_item`] ? "border-red-400" : "border-gray-300"}`}
                            >
                              <option value="">Select Product</option>
                              {products.map((p, pIdx) => {
                                const pKey = getProductKey(p) || pIdx;
                                const pVal = getProductKey(p);
                                const pName = getProductName(p);
                                return (
                                  <option key={pKey} value={pVal}>
                                    {pName}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              value={line.description}
                              onChange={(e) => handleLineChange(index, "description", e.target.value)}
                              className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all min-w-[140px]"
                              placeholder="Description"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="1"
                              value={line.quantity}
                              onChange={(e) => handleLineChange(index, "quantity", e.target.value)}
                              className={`h-10 w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all text-right min-w-[65px] ${errors[`line_${index}_qty`] ? "border-red-400" : "border-gray-300"}`}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={line.rate}
                              onChange={(e) => handleLineChange(index, "rate", e.target.value)}
                              className="h-10 w-full border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all text-right min-w-[90px]"
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-800 whitespace-nowrap">
                            {formatCurrency(amount)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {formData.lines.length > 1 && (
                              <button type="button" onClick={() => removeLine(index)} className="text-red-400 hover:text-red-600 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end mt-5">
                <div className="bg-gray-50 rounded-2xl p-5 w-full max-w-md space-y-4">
                  <div className="flex justify-between items-center text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(subtotal)}</span>
                  </div>

                  {/* Discount */}
                  <div className="flex justify-between items-center text-sm text-gray-600 gap-4">
                    <span className="shrink-0">Discount</span>
                    <div className="flex items-center gap-2 ml-auto">
                      <input
                        type="number"
                        min="0"
                        name="discount_value"
                        className="w-20 p-1.5 text-right border rounded-xl text-xs text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.discount_value}
                        onChange={handleInputChange}
                      />
                      <select
                        name="discount_type"
                        className="p-1.5 border rounded-xl text-xs text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white"
                        value={formData.discount_type}
                        onChange={handleInputChange}
                      >
                        <option value="fixed">$</option>
                        <option value="percent">%</option>
                      </select>
                      <span className="font-semibold text-gray-800 w-24 text-right">
                        -{formatCurrency(discountAmt)}
                      </span>
                    </div>
                  </div>

                  {/* Sales Tax */}
                  <div className="flex justify-between items-center text-sm text-gray-600 gap-4">
                    <span className="shrink-0">Sales Tax</span>
                    <div className="flex items-center gap-2 ml-auto">
                      <select
                        name="taxItemListId"
                        className="p-1.5 border rounded-xl text-xs text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all border-gray-300 bg-white max-w-[180px]"
                        value={formData.taxItemListId}
                        onChange={(e) => {
                          const taxId = e.target.value;
                          const selectedTax = taxRates.find((t) => getProductKey(t) === taxId);
                          if (selectedTax) {
                            setFormData((prev) => ({
                              ...prev,
                              taxItemListId: taxId,
                              taxName: getProductName(selectedTax),
                              taxPercent: getTaxRate(selectedTax),
                            }));
                          } else {
                            setFormData((prev) => ({
                              ...prev,
                              taxItemListId: "",
                              taxName: "",
                              taxPercent: 0,
                            }));
                          }
                        }}
                      >
                        <option value="">Select Tax Code</option>
                        {taxRates.map((tax, taxIdx) => {
                          const key = getProductKey(tax) || taxIdx;
                          const val = getProductKey(tax);
                          const name = getProductName(tax);
                          const rate = getTaxRate(tax);
                          return (
                            <option key={key} value={val}>
                              {name} ({rate}%)
                            </option>
                          );
                        })}
                      </select>
                      <span className="font-semibold text-gray-800 w-24 text-right">
                        {formatCurrency(taxAmt)}
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center border-t pt-3">
                    <span className="text-base font-semibold text-gray-900">Total</span>
                    <span className="text-base font-bold text-[#115B9D]">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#115B9D] hover:opacity-80 text-white text-sm font-medium transition-all disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin size-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                    </svg>
                    Create Estimate
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Created Success & Web Connector Guidance Modal */}
      <Transition appear show={showSuccessModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            /* Closed only when clicking Okay Got It */
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6 border border-gray-200 text-left">
                  {/* Modal Header */}
                  <div className="flex items-start justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <div className="size-10 rounded-xl bg-[#F0FEF4] border border-[#0a7822a6]/20 text-[#008236] flex items-center justify-center shrink-0">
                        <CheckCircle2 className="size-5" />
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Estimate Created Successfully!
                        </Dialog.Title>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Queued for QuickBooks Desktop Web Connector
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                      <p className="text-xs font-semibold text-[#115B9D] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <PlayCircle className="size-4" /> Sync Instructions
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            1
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            If <strong>Auto-Run</strong> is not enabled in Web Connector, open <strong>QuickBooks Web Connector</strong> on your PC.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            2
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Check the box <strong className="text-[#115B9D]">[ ✓ ]</strong> next to your application name and click <strong className="text-[#008236]">"Update Selected"</strong> to manually sync this estimate.
                          </p>
                        </div>

                        <div className="flex items-start gap-2.5">
                          <span className="size-5 rounded-md bg-[#115B9D] text-white text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                            3
                          </span>
                          <p className="text-xs text-gray-700 leading-5">
                            Once synced, the estimate status and details will be updated in your desktop estimate listing.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200/80 flex items-start gap-2.5 text-xs text-amber-900 leading-5">
                      <Sparkles className="size-4 text-amber-600 mt-0.5 shrink-0" />
                      <span>
                        <strong>Pro Tip:</strong> Enable the <strong>Auto-Run</strong> checkbox in Web Connector to automatically sync new estimates every 15 minutes.
                      </span>
                    </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex justify-end">
                    <button
                      type="button"
                      onClick={handleModalClose}
                      className="inline-flex items-center justify-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-xs font-medium px-5 py-2.5 rounded-xl transition-all"
                    >
                      Okay, Got it!
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}

